import React from 'react';

import { css } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TextToast } from '@/components/toast/TextToast.tsx';
import { useStyles } from '@/hooks/useStyles';
import {
  AlertConfig,
  TimeAboveRangeCGM,
  alertCategoriesTranslations,
} from '@/models/AlertModel.ts';
import { HappenedInTheLastXDays } from '@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx';
import { NumberOfOccurrences } from '@/pages/patient-settings/alert-settings/inputs/NumberOfOccurrences.tsx';
import { SelectType } from '@/pages/patient-settings/alert-settings/inputs/SelectType.tsx';
import { Queries } from '@/queries/Queries.ts';
import { formatNumber } from '@/utils/mathUtils.ts';

export type AlertHyperglycemiaTarSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: TimeAboveRangeCGM;
};

export const AlertHyperglycemiaTarSettings: React.FC<
  AlertHyperglycemiaTarSettingsProps
> = ({ parameters, patientId, alertConfig }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();
  const updateParameters = (value: unknown, key: keyof TimeAboveRangeCGM) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t('alertConfig.conditionsSuccess', {
              alert: t(alertCategoriesTranslations.time_above_range),
            }),
          ),
        onError: () =>
          TextToast.error(
            t('alertConfig.conditionsError', {
              alert: t(alertCategoriesTranslations.time_above_range),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <NumberOfOccurrences
        textBefore={t('alertConfig.moreThan')}
        textAfter={`%`}
        onCountChange={count =>
          updateParameters(count / 100.0, 'duration_proportion')
        }
        minCount={0}
        maxCount={100}
        count={formatNumber(parameters.duration_proportion * 100, 0)}
      />
      <Typography variant="body">
        {t('alertConfig.timeSpentInHyper')}
      </Typography>
      <SelectType
        onSelectChange={type => updateParameters(type, 'severe_only')}
        options={[
          { value: false, text: t('alertConfig.highOrVeryHigh') },
          { value: true, text: t('alertConfig.veryHigh') },
        ]}
        selected={parameters.severe_only}
      />
      <HappenedInTheLastXDays
        onDaysChange={days => updateParameters(days, 'number_of_days')}
        minDays={1}
        maxDays={365}
        days={parameters.number_of_days}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
});
